/**
 * FlashcardSceneEditor — coach authoring form for flashcard scene type.
 *
 * Follows the exact pattern of GenericSceneEditorPage.js (dark-theme coach UI,
 * same input/label/card styles, same sceneStore → updateScene write path).
 *
 * Fields:
 *   Front Content      — rich text / markdown textarea (required)
 *   Back Content       — rich text / markdown textarea (required)
 *   Key Insight        — text field, required (red border if empty)
 *   Hint               — optional text field
 *   Skill ID           — dropdown populated from domain's adaptive_config skills
 *   Card Type          — dropdown: Concept | Scenario | Argument | Image Recall
 *   Difficulty         — dropdown: Beginner | Intermediate | Advanced
 *   Media URL          — optional URL field (shows image preview when valid)
 *
 * Props:
 *   sceneId  {string}   — scene_id of the flashcard scene being edited
 *   domain   {string}   — domain identifier (for loading skill list)
 */

import React, { useState, useEffect } from 'react';
import { useSceneStore } from '../../store/sceneStore';

const API_BASE = '/scenario-os';
const TOKEN_KEY = 'scenario_os_token';

function getToken() {
  return (
    localStorage.getItem(TOKEN_KEY) ||
    sessionStorage.getItem(TOKEN_KEY) ||
    ''
  );
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const CARD_TYPE_OPTIONS = [
  { value: 'concept',      label: 'Concept' },
  { value: 'scenario',     label: 'Scenario' },
  { value: 'argument',     label: 'Argument' },
  { value: 'image_recall', label: 'Image Recall' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'beginner',     label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced',     label: 'Advanced' },
];

export default function FlashcardSceneEditor({ sceneId, domain }) {
  const scene = useSceneStore((s) => s.scene);
  const updateScene = useSceneStore((s) => s.updateScene);

  const [skills, setSkills] = useState([]);
  const [mediaPreviewError, setMediaPreviewError] = useState(false);
  const [saved, setSaved] = useState(false);

  // Prefer domain_metadata; fall back to top-level seed fields (AI packs).
  const rawMeta = (scene && scene.domain_metadata) || {};
  const meta = {
    ...rawMeta,
    front_content: rawMeta.front_content || scene?.front_content || scene?.front || '',
    back_content: rawMeta.back_content || scene?.back_content || scene?.back || '',
    key_insight: rawMeta.key_insight || scene?.key_insight || '',
    hint_text: rawMeta.hint_text || scene?.hint_text || scene?.hint || '',
    skill_id: rawMeta.skill_id || scene?.skill_id || '',
    card_type: rawMeta.card_type || scene?.card_type || 'concept',
    difficulty: rawMeta.difficulty || scene?.difficulty || 'intermediate',
    media_url: rawMeta.media_url || scene?.media_url || '',
  };

  // Load skill list from domain adaptive config
  useEffect(() => {
    if (!domain) return;
    let cancelled = false;
    async function loadSkills() {
      try {
        const res = await fetch(
          `${API_BASE}/adaptive/item-bank/${encodeURIComponent(domain)}/overview`,
          { headers: authHeaders() }
        );
        if (!res.ok) {
          // Fallback: fetch scene types to extract skill_id references
          const r2 = await fetch(
            `${API_BASE}/coach/domain/${encodeURIComponent(domain)}/scene-types`,
            { headers: authHeaders() }
          );
          if (r2.ok) {
            // If no dedicated skill endpoint, use placeholder list
            if (!cancelled) setSkills([]);
          }
          return;
        }
        const data = await res.json();
        if (!cancelled && Array.isArray(data.skills)) {
          setSkills(data.skills);
        }
      } catch (_e) {
        // Non-critical — coach can still type skill_id manually
      }
    }
    loadSkills();
    return () => { cancelled = true; };
  }, [domain]);

  if (!scene) return null;

  const updateMeta = (patch) => {
    setSaved(false);
    updateScene({
      ...scene,
      domain_metadata: { ...meta, ...patch },
    });
  };

  const handleSave = async () => {
    // Persist via PATCH /coach/scenes/{sceneId}
    try {
      await fetch(
        `${API_BASE}/coach/scenes/${encodeURIComponent(sceneId)}`,
        {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ domain_metadata: meta }),
        }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('[FlashcardSceneEditor] save failed:', err);
    }
  };

  // Validation state
  const missingKeyInsight = !(meta.key_insight || '').trim();
  const missingFront = !(meta.front_content || '').trim();
  const missingBack = !(meta.back_content || '').trim();

  return (
    <div style={editorContainerStyle}>
      <div style={sectionHeadingStyle}>Flashcard Fields</div>

      {/* Front Content */}
      <div style={fieldGroupStyle}>
        <div style={{ ...labelStyle, ...(missingFront ? { color: '#f87171' } : {}) }}>
          Front Content (Question) {missingFront && '— required'}
        </div>
        <textarea
          rows={4}
          value={meta.front_content || ''}
          onChange={(e) => updateMeta({ front_content: e.target.value })}
          placeholder="Enter the question or concept prompt. Markdown supported."
          style={{
            ...inputStyle,
            ...(missingFront ? { borderColor: '#f87171' } : {}),
            resize: 'vertical',
          }}
        />
        <div style={helperTextStyle}>Supports **bold**, *italic*, `code`</div>
      </div>

      {/* Back Content */}
      <div style={fieldGroupStyle}>
        <div style={{ ...labelStyle, ...(missingBack ? { color: '#f87171' } : {}) }}>
          Back Content (Answer) {missingBack && '— required'}
        </div>
        <textarea
          rows={4}
          value={meta.back_content || ''}
          onChange={(e) => updateMeta({ back_content: e.target.value })}
          placeholder="Enter the full answer or explanation. Markdown supported."
          style={{
            ...inputStyle,
            ...(missingBack ? { borderColor: '#f87171' } : {}),
            resize: 'vertical',
          }}
        />
      </div>

      {/* Key Insight */}
      <div style={fieldGroupStyle}>
        <div style={{ ...labelStyle, ...(missingKeyInsight ? { color: '#f87171' } : {}) }}>
          Key Insight {missingKeyInsight && '— required to publish'}
        </div>
        <input
          type="text"
          value={meta.key_insight || ''}
          onChange={(e) => updateMeta({ key_insight: e.target.value })}
          placeholder="The single most important principle the learner should retain (1–2 sentences)"
          style={{
            ...inputStyle,
            ...(missingKeyInsight ? { borderColor: '#f87171' } : {}),
          }}
        />
        {missingKeyInsight && (
          <div style={{ ...helperTextStyle, color: '#f87171' }}>
            A flashcard cannot be published without a Key Insight.
          </div>
        )}
      </div>

      {/* Hint */}
      <div style={fieldGroupStyle}>
        <div style={labelStyle}>Hint (Optional)</div>
        <input
          type="text"
          value={meta.hint_text || ''}
          onChange={(e) => updateMeta({ hint_text: e.target.value || null })}
          placeholder="Progressive hint shown if learner hesitates (optional)"
          style={inputStyle}
        />
      </div>

      {/* Skill ID */}
      <div style={fieldGroupStyle}>
        <div style={labelStyle}>Skill ID</div>
        {skills.length > 0 ? (
          <select
            value={meta.skill_id || ''}
            onChange={(e) => updateMeta({ skill_id: e.target.value })}
            style={inputStyle}
          >
            <option value="">— Select a skill —</option>
            {skills.map((sk) => (
              <option key={sk.skill_id || sk} value={sk.skill_id || sk}>
                {sk.display_name || sk.skill_id || sk}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={meta.skill_id || ''}
            onChange={(e) => updateMeta({ skill_id: e.target.value })}
            placeholder="e.g. gradient_descent"
            style={inputStyle}
          />
        )}
      </div>

      {/* Card Type */}
      <div style={fieldGroupStyle}>
        <div style={labelStyle}>Card Type</div>
        <select
          value={meta.card_type || 'concept'}
          onChange={(e) => updateMeta({ card_type: e.target.value })}
          style={inputStyle}
        >
          {CARD_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Difficulty */}
      <div style={fieldGroupStyle}>
        <div style={labelStyle}>Difficulty</div>
        <select
          value={meta.difficulty || 'intermediate'}
          onChange={(e) => updateMeta({ difficulty: e.target.value })}
          style={inputStyle}
        >
          {DIFFICULTY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Media URL */}
      <div style={fieldGroupStyle}>
        <div style={labelStyle}>Media URL (Optional)</div>
        <input
          type="url"
          value={meta.media_url || ''}
          onChange={(e) => {
            setMediaPreviewError(false);
            updateMeta({ media_url: e.target.value || null });
          }}
          placeholder="https://example.com/image.png"
          style={inputStyle}
        />
        {meta.media_url && !mediaPreviewError && (
          <img
            src={meta.media_url}
            alt="Media preview"
            onError={() => setMediaPreviewError(true)}
            style={mediaPreviewStyle}
          />
        )}
        {mediaPreviewError && (
          <div style={{ ...helperTextStyle, color: '#f87171' }}>
            Image failed to load. Check the URL.
          </div>
        )}
      </div>

      {/* Save button */}
      <div style={saveRowStyle}>
        <button
          onClick={handleSave}
          disabled={missingKeyInsight || missingFront || missingBack}
          style={{
            ...saveBtnStyle,
            opacity: (missingKeyInsight || missingFront || missingBack) ? 0.5 : 1,
            cursor: (missingKeyInsight || missingFront || missingBack) ? 'not-allowed' : 'pointer',
          }}
        >
          {saved ? '✓ Saved' : 'Save Flashcard'}
        </button>
        {(missingKeyInsight || missingFront || missingBack) && (
          <div style={{ ...helperTextStyle, color: '#f87171' }}>
            Fill all required fields before saving.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles (dark coach UI — matches GenericSceneEditorPage) ───────────────────

const editorContainerStyle = {
  marginTop: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
};

const sectionHeadingStyle = {
  fontSize: '0.8rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '1rem',
};

const fieldGroupStyle = {
  background: '#1a1d2e',
  border: '1px solid #2d3748',
  borderRadius: '8px',
  padding: '1rem 1.25rem',
  marginBottom: '0.75rem',
};

const labelStyle = {
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#64748b',
  marginBottom: '0.5rem',
};

const inputStyle = {
  width: '100%',
  background: '#0f1117',
  border: '1px solid #2d3748',
  borderRadius: '4px',
  color: '#e2e8f0',
  padding: '6px 8px',
  fontSize: '0.82rem',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  outline: 'none',
};

const helperTextStyle = {
  fontSize: '0.72rem',
  color: '#64748b',
  marginTop: '0.35rem',
};

const mediaPreviewStyle = {
  marginTop: '8px',
  maxWidth: '100%',
  maxHeight: '160px',
  objectFit: 'contain',
  borderRadius: '6px',
  border: '1px solid #2d3748',
};

const saveRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginTop: '0.5rem',
};

const saveBtnStyle = {
  background: '#3b82f6',
  color: '#fff',
  border: '1px solid #3b82f6',
  borderRadius: '4px',
  padding: '8px 20px',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
};
